import { useState } from 'react';
import api from '@/utils/ky';

export interface PersonaAssessmentComponent {
  name: string;
  score: number;
  maxScore: number;
  feedback: string;
}

export interface PersonaAssessment {
  summary: string;
  score: number;
  rating: 'Exceptional' | 'Good' | 'Adequate' | 'Needs Improvement' | 'Unsatisfactory';
  components: PersonaAssessmentComponent[];
  strengths: string[];
  areasForImprovement: string[];
  examples: string[];
}

export interface PersonaAssessmentResponse {
  success: boolean;
  sessionId: string;
  personaId: string;
  scenarioCode: string;
  tooBrief: boolean;
  assessment: PersonaAssessment;
}

export function usePersonaAssessment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<PersonaAssessment | null>(null);

  const generateAssessment = async (
    sessionId: string,
    transcript?: string
  ): Promise<PersonaAssessment | null> => {
    // Prevent duplicate requests
    if (loading) {
      console.log('Assessment already in progress, skipping...');
      return null;
    }

    setLoading(true);
    setError(null);
    setAssessment(null);

    try {
      console.log('Generating persona assessment for session:', sessionId);

      const response = await api
        .post(`api/agent/sessions/${sessionId}/persona-assessment`, {
          json: { transcript },
          timeout: 60000, // 60 second timeout
        })
        .json<PersonaAssessmentResponse>();

      console.log('Assessment response:', response);

      if (response.tooBrief) {
        setError('Transcript is too short for meaningful assessment');
        return null;
      }

      setAssessment(response.assessment);
      return response.assessment;
    } catch (err: any) {
      console.error('Assessment error:', err);

      // Don't retry on 404 (session not found) or 400 (bad request)
      if (err?.response?.status === 404) {
        setError('Session not found. Please create a new session.');
      } else if (err?.response?.status === 400) {
        setError('Invalid request. Please check the session data.');
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate assessment';
        setError(errorMessage);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const clearAssessment = () => {
    setAssessment(null);
    setError(null);
  };

  return {
    generateAssessment,
    clearAssessment,
    assessment,
    loading,
    error
  };
}
